import { useEffect, useState } from 'react';
import useSWR from 'swr';
import { consignmentService } from 'services';
import { useAuth } from 'hooks';
import { Consignment } from 'types';
import { Header, Heading, Main, Select, Text } from 'components';
import { swrKeys } from 'helpers';
import { FontWeight, STATUSES } from 'commons';
import { ProductList } from './components';
import * as S from './styles';
import { FilterOption, ProductsToPickup, filterFormOptions } from './types';

export function PickingManagement(): JSX.Element {
  const { selectedStore } = useAuth();

  const storeNames = selectedStore.name || '';

  const [filter, setFilter] = useState(FilterOption.hour);
  const [productsToPickup, setProductsToPickup] = useState<ProductsToPickup[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(true);

  const initialSelection = filterFormOptions.find(
    ({ value }) => value === FilterOption.hour
  );

  const { data } = useSWR(
    swrKeys.consignments({ key: `${storeNames}-picking-management` }),
    () =>
      consignmentService.get({
        statuses: `${STATUSES.AVAILABLE_FOR_STORE.value}, ${STATUSES.RESERVED_BY_STORE.value}`,
        storeNames,
      }),
    {
      refreshWhenHidden: true,
      revalidateOnFocus: false,
    }
  );

  useEffect(() => {
    if (!data) return;

    setIsLoading(true);

    const productsPromise = data.reduce<Promise<ProductsToPickup[]>>(
      async (acc: Promise<ProductsToPickup[]>, consignment: Consignment) => {
        const { consignmentCode, startTimer } = consignment;
        const response = await consignmentService.getDetails(consignmentCode);
        const consignmentProducts: ProductsToPickup[] = response.entries.map(
          ({ product, quantity }) => ({
            consignmentCode,
            quantity,
            availableAt: startTimer,
            ...product,
          })
        );

        const currAcc = await acc;

        return currAcc.concat(
          /*
           * verify the existence of same product SKU
           * within the consignment and add an identifier
           * based on the index
           */
          consignmentProducts.map((product, index) => {
            const existingProduct = currAcc.find(
              (p) =>
                p.consignmentCode === product.consignmentCode &&
                p.legacySKU === product.legacySKU
            );

            if (existingProduct) {
              product.legacySKU = `${product.legacySKU}_${index}`;
            }

            return product;
          })
        );
      },
      Promise.resolve([] as ProductsToPickup[])
    );

    productsPromise.then((products) => {
      setProductsToPickup(products);
      setIsLoading(false);
    });
  }, [data]);

  function handleChange(e: any): void {
    const { target } = e;

    if (target && typeof target.value === 'string') {
      setFilter(target.value);
    }
  }

  return (
    <Main>
      <Header />

      <S.Main>
        <S.TextWrapper>
          <Heading weight={FontWeight.bold}>Separação de produtos</Heading>
          <Text>
            Relatório contendo todos os produtos de todos os pedidos que estão
            com status <strong>AGUARDANDO ATENDIMENTO</strong> e{' '}
            <strong>EM ATENDIMENTO</strong>, separados por hora de
            disponibilização do pedido. Se desejar, marque os produtos que você
            já separou através do checkbox para um controle visual.
          </Text>
        </S.TextWrapper>

        <S.FilterWrapper>
          <Select.Wrapper
            data-testid={'select'}
            onChange={handleChange}
            name="hourFilter"
            initialSelected={initialSelection}
            options={filterFormOptions}
          >
            <Select.Input placeholder="Selecione..." />

            <Select.StatusArrow />
          </Select.Wrapper>
        </S.FilterWrapper>
        <ProductList
          filter={filter}
          isLoading={isLoading}
          productsToPickup={productsToPickup}
        />
      </S.Main>
    </Main>
  );
}
